/**
 * HealthTab — how much one company uses the platform (SF-5096). Read only.
 *
 * One request (GET /admin/usage/company/:id) feeds every section; each section shows that
 * request's loading or error state on its own card. The page mounts this keyed by company id,
 * so the chosen month starts again on each company. SF-5117: the month in progress banner, the
 * % change and per-day columns (like-for-like while in progress), and the Eva card. SF-5124: the
 * comparison table and the 12-month chart each get a full-width row, then Feedback by day and
 * Features side by side; cards match in height and stack one per row below lg. SF-5175: a
 * company not counted in platform usage (Demo, Test, inactive, ServeFirst Live) gets its note at
 * the top and its figures below.
 */
import { useState } from 'react';
import { Alert, Col, Row, Space, Tag, Typography } from 'antd';
import { TrendChart } from '../../../../components/charts/TrendChart';
import { MonthPicker } from '../../../../components/usage/MonthPicker';
import { MonthProgressBanner } from '../../../../components/usage/MonthProgressBanner';
import { UsageLayers } from '../../../../components/usage/UsageLayers';
import { UsageSection } from '../../../../components/usage/UsageSection';
import { useCompanyUsage, type CompanyUsage } from '../../../../components/usage/usageClient';
import { currentUsagePeriod, formatComputedAt } from '../../../../components/usage/usageNumbers';
import { comparisonCaption, isInProgress } from '../../../../components/usage/usageProgress';
import {
  DAILY_TREND_DAYS,
  formatBucketLabel,
  MONTHLY_TREND_MONTHS,
  priorPeriod,
} from '../../../../utils/date';
import { formatNumber } from '../../../../utils/format';
import { CompanyFeaturesTable } from './CompanyFeaturesTable';
import { EvaCard } from './EvaCard';
import { notCountedNote } from './healthText';

const { Text } = Typography;

export const HealthTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  // Only a month the user picked is stored, so a tab left open over a month end moves on. This
  // month is London's, as the API counts it.
  const [pickedPeriod, setPickedPeriod] = useState<string | null>(null);
  const period = pickedPeriod ?? currentUsagePeriod();
  const onPeriodChange = (p: string) => setPickedPeriod(p === currentUsagePeriod() ? null : p);

  const usage = useCompanyUsage(companyId, period);
  const answer = usage.data?.data;
  // Figures are shown only when they are for this company and month. The query key already
  // holds both; this also covers any cached answer that does not match what is on screen.
  const data: CompanyUsage | undefined =
    answer && String(answer.company?.id) === companyId && answer.period === period
      ? answer
      : undefined;
  const mismatched = answer !== undefined && data === undefined;
  const state = {
    loading: usage.isLoading || (mismatched && usage.isFetching),
    error: usage.isError || (mismatched && !usage.isFetching),
    onRetry: () => void usage.refetch(),
  };

  const note = data ? notCountedNote(data) : null;
  const updated = formatComputedAt(data?.computed_at);
  const monthLabel = formatBucketLabel(period, 'monthly');
  const lastMonthLabel = formatBucketLabel(priorPeriod(period), 'monthly');
  const monthly = (data?.monthly ?? []).map((m) => ({
    bucket: m.period,
    value: m.totals.total_feedback,
  }));
  const daily = (data?.daily ?? []).map((d) => ({ bucket: d.date, value: d.total_feedback }));
  const caption = comparisonCaption(data?.progress);
  // Per-day columns only when the API sends the averages (an older API does not).
  const perDay =
    data && data.this_month_daily_average !== undefined
      ? {
          current: data.this_month_daily_average ?? null,
          previous: data.last_month_daily_average ?? null,
        }
      : undefined;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="health-tab">
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col>
          <Space wrap>
            {data && (
              <Text type="secondary">
                {updated ? `Last updated: ${updated}` : 'Not counted yet for this month'}
              </Text>
            )}
            {data?.on_old_ratings && <Tag color="orange">Still on old style ratings</Tag>}
          </Space>
        </Col>
        <Col>
          <MonthPicker value={period} onChange={onPeriodChange} />
        </Col>
      </Row>

      {note && <Alert type="info" showIcon message={note} data-testid="health-not-counted" />}

      <MonthProgressBanner progress={data?.progress} />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <UsageSection
            title={
              isInProgress(data?.progress)
                ? 'This month vs same days last month'
                : 'This month vs last month'
            }
            subtitle={
              data
                ? `Total feedback ${formatNumber(data.this_month.total_feedback)}, against ${formatNumber(data.last_month.total_feedback)}${isInProgress(data.progress) ? ' on the same days' : ''}`
                : undefined
            }
            {...state}
          >
            {data && (
              <UsageLayers
                totals={data.this_month}
                label="Feedback by type"
                compare={{
                  previous: data.last_month,
                  currentLabel: monthLabel,
                  previousLabel: lastMonthLabel,
                  changeLabel: `Change ${caption}`,
                  perDay,
                }}
              />
            )}
          </UsageSection>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <HealthChart
            title="Total feedback by month"
            subtitle={`Last ${MONTHLY_TREND_MONTHS} months to ${monthLabel}`}
            points={monthly}
            granularity="monthly"
            {...state}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={12}>
          <HealthChart
            title="Feedback by day"
            subtitle={`Last ${DAILY_TREND_DAYS} days, in the company’s time zone`}
            points={daily}
            granularity="daily"
            {...state}
          />
        </Col>
        <Col xs={24} lg={12}>
          <UsageSection
            title="Features"
            {...state}
            empty={(data?.features ?? []).length === 0}
            emptyText="No features to show"
          >
            {data && <CompanyFeaturesTable features={data.features} />}
          </UsageSection>
        </Col>
      </Row>

      {data?.eva && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <UsageSection title="Eva" {...state}>
              <EvaCard
                eva={data.eva}
                currentLabel={monthLabel}
                previousLabel={lastMonthLabel}
                caption={caption}
              />
            </UsageSection>
          </Col>
        </Row>
      )}
    </Space>
  );
};

interface HealthChartProps {
  title: string;
  subtitle: string;
  points: Array<{ bucket: string; value: number }>;
  granularity: 'daily' | 'monthly';
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

/** A chart in the shared usage card, or the section's error when the request failed. */
const HealthChart: React.FC<HealthChartProps> = ({
  error,
  onRetry,
  points,
  subtitle,
  ...chart
}) => (
  <UsageSection
    title={chart.title}
    subtitle={error ? undefined : subtitle}
    loading={false}
    error={error}
    onRetry={onRetry}
  >
    <TrendChart bare {...chart} data={points} />
  </UsageSection>
);
